import React, { useState } from 'react';
import { Brain, HelpCircle, ArrowLeft, MessageSquare, Mail, Clock, ChevronDown, ChevronUp, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

interface FAQItem {
  question: string;
  answer: string;
}

const faqs: FAQItem[] = [
  {
    question: 'How do I create my school on CampusConnect?',
    answer: 'After signing up, you\'ll be prompted to create your school. Enter your school name, type (K-12, academy, or college), and a short description. Once created, you can invite team members from the Team page.'
  },
  {
    question: 'How do I invite teachers and staff to my school?',
    answer: 'Go to the Team page from the sidebar, then click "Invite Member." Enter their email address and select their role (Admin, Teacher, or Staff). They\'ll receive an email invitation to join your school on CampusConnect.'
  },
  {
    question: 'Can I connect CampusConnect to our existing LMS?',
    answer: 'Yes! CampusConnect supports integration with Canvas and Infinite Campus. Go to Settings > LMS Integration to connect your LMS. Once connected, data syncs automatically so you never have to double-enter information.'
  },
  {
    question: 'How does the AI-powered automation work?',
    answer: 'CampusConnect uses AI to summarize long message threads, suggest task assignments, auto-route attendance issues, and provide smart notifications. The AI learns your school\'s patterns over time to provide increasingly relevant suggestions.'
  },
  {
    question: 'Is my school\'s data secure?',
    answer: 'Absolutely. All data is encrypted in transit and at rest. Each school\'s data is completely isolated — no other school can access your information. We use Firebase security rules to enforce strict access controls, and payments are processed securely through Stripe.'
  },
  {
    question: 'Can I use CampusConnect on my phone?',
    answer: 'Yes! CampusConnect is a Progressive Web App (PWA) that works great on mobile browsers. You can even add it to your home screen for a native app experience. The interface automatically adapts to mobile screens.'
  },
  {
    question: 'How do I upgrade or change my subscription?',
    answer: 'Go to the Billing page from the sidebar. You can view your current plan, upgrade to a higher tier, or manage your subscription. Changes take effect immediately, and you\'ll be prorated for the remainder of your billing cycle.'
  },
  {
    question: 'What happens if I cancel my subscription?',
    answer: 'If you cancel, you\'ll retain access to paid features until the end of your current billing period. After that, your school reverts to the Free plan. Your data is never deleted — you can resubscribe at any time to regain access to premium features.'
  },
  {
    question: 'Can multiple administrators manage the same school?',
    answer: 'Yes! You can have multiple administrators for your school. Go to Team > Invite Member and assign the "Admin" role. Admins can manage settings, billing, team members, and all school data.'
  },
  {
    question: 'How do I sync my Google Calendar?',
    answer: 'Go to Settings > Calendar Settings and click "Connect Google Calendar." You\'ll be prompted to authorize CampusConnect to access your calendar. Once connected, events sync both ways — create events in CampusConnect and they appear in Google Calendar, and vice versa.'
  },
];

export default function SupportPage() {
  useDocumentTitle('Support');
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-gradient-to-br from-background-light to-background-dark">
      {/* Navigation */}
      <nav className="border-b border-border-light bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <Brain className="w-8 h-8 text-primary-500" />
              <span className="text-xl font-bold text-text-primary">Campus Connect</span>
            </Link>
            <Link to="/" className="flex items-center gap-2 text-text-secondary hover:text-primary-500 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-12 max-w-3xl">
        <div className="flex items-center gap-3 mb-4">
          <HelpCircle className="w-10 h-10 text-primary-500" />
          <h1 className="text-3xl font-bold text-text-primary">Support Center</h1>
        </div>
        <p className="text-text-secondary mb-8">Find answers to common questions or reach out to our team.</p>

        {/* Quick Links */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
          <Link to="/getting-started"
            className="bg-surface-light rounded-lg shadow-md p-5 hover:shadow-lg transition-shadow text-center group">
            <BookOpen className="w-8 h-8 text-secondary-500 mx-auto mb-2 group-hover:scale-110 transition-transform" />
            <h3 className="font-semibold text-text-primary text-sm">Getting Started Guide</h3>
            <p className="text-xs text-text-muted mt-1">Step-by-step setup walkthrough</p>
          </Link>
          <a href="mailto:support@sketchmyapp.com"
            className="bg-surface-light rounded-lg shadow-md p-5 hover:shadow-lg transition-shadow text-center group">
            <Mail className="w-8 h-8 text-primary-500 mx-auto mb-2 group-hover:scale-110 transition-transform" />
            <h3 className="font-semibold text-text-primary text-sm">Email Support</h3>
            <p className="text-xs text-text-muted mt-1">support@sketchmyapp.com</p>
          </a>
          <div className="bg-surface-light rounded-lg shadow-md p-5 text-center">
            <Clock className="w-8 h-8 text-accent-500 mx-auto mb-2" />
            <h3 className="font-semibold text-text-primary text-sm">Response Time</h3>
            <p className="text-xs text-text-muted mt-1">Within 24 hours, Mon-Fri</p>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mb-10">
          <div className="flex items-center gap-2 mb-6">
            <MessageSquare className="w-6 h-6 text-primary-500" />
            <h2 className="text-xl font-semibold text-text-primary">Frequently Asked Questions</h2>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-surface-light rounded-lg shadow-sm overflow-hidden">
                <button
                  onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-primary-50/30 transition-colors"
                >
                  <span className="font-medium text-text-primary text-sm pr-4">{faq.question}</span>
                  {openFAQ === index ? (
                    <ChevronUp className="w-5 h-5 text-primary-500 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-text-muted flex-shrink-0" />
                  )}
                </button>
                {openFAQ === index && (
                  <div className="px-4 pb-4 border-t border-border-light">
                    <p className="text-text-secondary text-sm leading-relaxed pt-3">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Still Need Help */}
        <div className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-xl p-8 text-center">
          <h3 className="text-lg font-semibold text-text-primary mb-2">Still need help?</h3>
          <p className="text-text-secondary text-sm mb-4">
            Our team is here to help you get the most out of CampusConnect.
          </p>
          <a href="mailto:support@sketchmyapp.com"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors">
            <Mail className="w-5 h-5" />
            Contact Support
          </a>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-text-muted">
          <div className="flex justify-center gap-6">
            <Link to="/privacy" className="hover:text-primary-500 transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-primary-500 transition-colors">Terms of Service</Link>
            <Link to="/getting-started" className="hover:text-primary-500 transition-colors">Getting Started</Link>
          </div>
        </div>
      </div>
    </div>
  );
}